import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import useIsLoggedIn from "../hooks/use-IsLoggedIn";

const CloseIcon = ({ className }) => (
	<svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
	</svg>
);

const ArrowRightIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
	</svg>
);

const SparkleIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z"
		/>
	</svg>
);

const SignUpPrompt = () => {
	const isLoggedIn = useIsLoggedIn();
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (isLoggedIn) return;

		const hasBeenDismissed = localStorage.getItem("signUpPromptDismissed") === "true";
		if (hasBeenDismissed) return;

		const timer = setTimeout(() => {
			setIsOpen(true);
		}, 3000);

		return () => clearTimeout(timer);
	}, [isLoggedIn]);

	if (isLoggedIn) return null;

	const handleClose = () => {
		setIsOpen(false);
		localStorage.setItem("signUpPromptDismissed", "true");
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<>
					{/* Mobile overlay */}
					<motion.div
						className="fixed inset-0 bg-black bg-opacity-30 z-[998] md:hidden"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={handleClose}
					/>

					{/* Mobile drawer */}
					<motion.div
						className="fixed bottom-0 left-0 right-0 rounded-t-3xl shadow-2xl z-[999] md:hidden overflow-hidden"
						initial={{ y: "100%" }}
						animate={{ y: 0 }}
						exit={{ y: "100%" }}
						transition={{ type: "spring", stiffness: 300, damping: 30 }}
					>
						{/* Gradient header */}
						<div className="bg-gradient-to-r from-primary-700 to-primary-500 px-6 pt-5 pb-4 relative">
							<button
								className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
								onClick={handleClose}
								aria-label="Close"
							>
								<CloseIcon className="h-6 w-6" />
							</button>
							<div className="flex items-center gap-2 mb-1">
								<SparkleIcon />
								<h3 className="text-xl font-bold text-white font-plus-jakarta">Join Dproz Today</h3>
							</div>
						</div>

						{/* Body */}
						<div className="bg-white px-6 pt-4 pb-8">
							<p className="text-sm text-gray-700 mb-1 font-inter">
								Create a free account to save jobs, get personalized recommendations, and track your applications.
							</p>
							<p className="text-xs text-primary-700 font-medium mb-5 font-inter">
								It's free — no credit card needed
							</p>
							<Link
								to="/register"
								onClick={handleClose}
								className="flex items-center justify-center gap-2 w-full bg-primary-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-800 transition-colors shadow-lg shadow-primary-700/20 font-inter"
							>
								Create Account
								<ArrowRightIcon />
							</Link>
						</div>
					</motion.div>

					{/* Desktop card */}
					<motion.div
						className="hidden md:block fixed bottom-6 right-6 w-80 rounded-xl shadow-2xl z-[999] overflow-hidden border border-primary-100"
						initial={{ opacity: 0, y: 40, x: 20 }}
						animate={{ opacity: 1, y: 0, x: 0 }}
						exit={{ opacity: 0, y: 40, x: 20 }}
						transition={{ type: "spring", stiffness: 300, damping: 25 }}
					>
						{/* Gradient header */}
						<div className="bg-gradient-to-r from-primary-700 to-primary-500 px-5 pt-4 pb-3 relative">
							<button
								className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
								onClick={handleClose}
								aria-label="Close"
							>
								<CloseIcon className="h-5 w-5" />
							</button>
							<div className="flex items-center gap-2">
								<SparkleIcon />
								<h3 className="text-base font-bold text-white font-plus-jakarta">Join Dproz Today</h3>
							</div>
						</div>

						{/* Body */}
						<div className="bg-white px-5 pt-3 pb-4">
							<p className="text-sm text-gray-700 mb-1 font-inter">
								Create a free account to save jobs, get personalized recommendations, and track your applications.
							</p>
							<p className="text-xs text-primary-700 font-medium mb-4 font-inter">
								It's free — no credit card needed
							</p>
							<Link
								to="/register"
								onClick={handleClose}
								className="flex items-center justify-center gap-2 w-full bg-primary-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-800 transition-colors shadow-lg shadow-primary-700/20 font-inter"
							>
								Create Account
								<ArrowRightIcon />
							</Link>
						</div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
};

export default SignUpPrompt;
